import { useCallback, useEffect, useRef, useState } from 'react'

interface Props {
	/** Classes applied to the inner scrollable div */
	className?: string
	/** Classes applied to the outer relative wrapper */
	wrapperClassName?: string
	/**
	 * When provided, re-measures scroll overflow each time this value changes
	 * to true (e.g. pass `isModalOpen` for components that stay mounted while
	 * the modal is closed).
	 */
	triggerOpen?: boolean
	children: React.ReactNode
}

/**
 * Wraps any scrollable area and renders a persistent custom scrollbar
 * indicator on mobile (<768 px) that is visible without requiring the user
 * to start scrolling first.  Works on iOS Safari and Android Chrome alike
 * because the indicator is plain DOM, not CSS scrollbar APIs.
 */
export const ScrollableWithIndicator = ({
	children,
	className,
	wrapperClassName,
	triggerOpen,
}: Props) => {
	const scrollRef = useRef<HTMLDivElement>(null)
	const [indicator, setIndicator] = useState({
		visible: false,
		thumbTop: 0,
		thumbHeight: 0,
		trackHeight: 0,
	})

	const updateIndicator = useCallback(() => {
		const el = scrollRef.current
		if (!el) return
		const { scrollTop, scrollHeight, clientHeight } = el
		if (scrollHeight <= clientHeight) {
			setIndicator(s => (s.visible ? { ...s, visible: false } : s))
			return
		}
		const thumbH = Math.max((clientHeight / scrollHeight) * clientHeight, 24)
		const maxTop = clientHeight - thumbH
		const thumbT = (scrollTop / (scrollHeight - clientHeight)) * maxTop
		setIndicator({ visible: true, thumbTop: thumbT, thumbHeight: thumbH, trackHeight: clientHeight })
	}, [])

	useEffect(() => {
		// Skip measurement if the caller explicitly signals "closed"
		if (triggerOpen === false) return
		const id = setTimeout(updateIndicator, 50)
		return () => clearTimeout(id)
	}, [triggerOpen, children, updateIndicator])

	return (
		<div className={`relative ${wrapperClassName ?? ''}`}>
			<div
				ref={scrollRef}
				onScroll={updateIndicator}
				className={`hide-native-scrollbar ${className ?? ''}`}
				style={{ scrollbarWidth: 'none' }}
			>
				{children}
			</div>
			{indicator.visible && (
				<div
					className='md:hidden absolute right-0 top-0 w-[3px] rounded-full overflow-hidden'
					style={{ height: indicator.trackHeight, backgroundColor: '#e5e7eb' }}
					aria-hidden='true'
				>
					<div
						className='absolute w-full rounded-full'
						style={{
							top: indicator.thumbTop,
							height: indicator.thumbHeight,
							backgroundColor: '#9ca3af',
							transition: 'top 60ms linear',
						}}
					/>
				</div>
			)}
		</div>
	)
}
